import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import CardBack from '../../tarot-components/CardBack'
import { getAllCards } from '../../tarot-data/cards'
import { shuffleCards } from '../../tarot-utils/cardUtils'
import { useCardLocalizer } from '../../i18n/useLocalized'
import { cardImage, saveDrawnCard, track } from './landingUtils'

const FAN_SIZE = 7

const dealHand = () => shuffleCards(getAllCards()).slice(0, FAN_SIZE)

/**
 * "Draw your card": a small fan of face-down cards. Choosing one brings it to
 * the centre and turns it over, shows its name and keywords, and invites the
 * visitor to sign up to hear the full reading. Runs entirely in the browser.
 *
 * The chosen card is saved so the dashboard can greet them with it after
 * they sign up (see DrawnCardBanner).
 */
const DrawTeaser = () => {
    const { t } = useTranslation()
    const localize = useCardLocalizer()
    const [hand, setHand] = useState(dealHand)
    const [chosen, setChosen] = useState(null)
    const [round, setRound] = useState(0)
    const resultRef = useRef(null)

    const choose = useCallback((index) => {
        if (chosen !== null) return
        setChosen(index)
        saveDrawnCard(hand[index].id)
        track('teaser_draw', { card: hand[index].name })
    }, [chosen, hand])

    const again = () => {
        setChosen(null)
        setHand(dealHand())
        setRound((r) => r + 1)
    }

    // Move focus to the result once the card has turned, so keyboard and
    // screen-reader users land on what was revealed
    useEffect(() => {
        if (chosen === null) return undefined
        const id = window.setTimeout(() => resultRef.current?.focus({ preventScroll: true }), 700)
        return () => window.clearTimeout(id)
    }, [chosen])

    const card = chosen !== null ? localize(hand[chosen]) : null
    const middle = (FAN_SIZE - 1) / 2

    return (
        <section className="arcana-section draw" aria-labelledby="draw-heading">
            <div className="arcana__inner">
                <header className="arcana__section-head reveal">
                    <p className="arcana__eyebrow">{t('public.draw.eyebrow')}</p>
                    <h2 id="draw-heading" className="arcana__h2">{t('public.draw.heading')}</h2>
                    <p className="arcana__section-text">{t('public.draw.hint')}</p>
                </header>

                {/* The state class sits inside the .reveal wrapper: React rewriting
                    className would otherwise strip the class useReveal added */}
                <div className="reveal">
                <div className={`draw__table ${chosen !== null ? 'has-chosen' : ''}`}>
                    <ul className="draw__fan" key={round}>
                        {hand.map((c, i) => {
                            const offset = i - middle
                            const isChosen = chosen === i
                            const img = cardImage(c.filename, '(max-width: 560px) 34vw, 180px')
                            return (
                                <li
                                    key={c.id}
                                    className={`draw__slot ${isChosen ? 'is-chosen' : ''}`}
                                    style={{
                                        '--off': offset,
                                        '--abs': Math.abs(offset),
                                        '--i': i,
                                    }}
                                >
                                    <button
                                        type="button"
                                        className="draw__card"
                                        onClick={() => choose(i)}
                                        disabled={chosen !== null}
                                        aria-label={t('public.draw.cardLabel', { n: i + 1, total: FAN_SIZE })}
                                    >
                                        <span className="flip">
                                            <span className="flip__face flip__face--back">
                                                <CardBack />
                                            </span>
                                            <span className="flip__face flip__face--front">
                                                <img {...img} alt="" loading="lazy" decoding="async" />
                                            </span>
                                        </span>
                                    </button>
                                </li>
                            )
                        })}
                    </ul>
                </div>
                </div>

                <div className="draw__result" aria-live="polite">
                    {card && (
                        <div className="draw__result-inner" ref={resultRef} tabIndex={-1}>
                            <p className="arcana__eyebrow">{t('public.draw.youDrew')}</p>
                            <h3 className="draw__name">{card.name}</h3>
                            <p className="draw__keywords">{card.keywords}</p>
                            <p className="draw__invite">{t('public.draw.invite', { card: card.name })}</p>
                            <div className="arcana__actions">
                                <Link
                                    to="/register"
                                    className="arcana-btn"
                                    onClick={() => track('cta_click', { where: 'teaser' })}
                                >
                                    {t('public.draw.cta')}
                                    <span className="arcana-btn__arrow" aria-hidden="true">&#8594;</span>
                                </Link>
                                <button type="button" className="arcana__link arcana__link--button" onClick={again}>
                                    {t('public.draw.again')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}

export default DrawTeaser
